import { Button } from "@/components/ui/Button";
import { PermissionButton } from "@/components/ui/PermissionButton";
import CountBox from "@/components/ui/CountBox";
import { InputBox } from "@/components/ui/InputBox";
import { CheckBox } from "@/components/ui/CheckBox";
import SelectBox from "@/components/ui/SelectBox";
import Table, { TableColumn } from "@/components/ui/Table";
import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useEmployeeCode } from "@/hooks/standard/st_employee_code/useEmployeeCode";
import { EmployeeCode } from "@/interface/standard/st_employee_code";
import AddModal, { EmployeeFormData } from "@/components/modal/standard/employeeCode/Add";
import UpdateModal from "@/components/modal/standard/employeeCode/Update";
import { toast } from "sonner";
import { useEmployeeCodeResetPassword, useEmployeeCodeDelete } from "@/hooks/standard/st_employee_code/useEmployeeCode";
import PrintButton from "@/components/ui/PrintButton";
import ExcelButton from "@/components/ui/ExcelButton";

export default function EmployeeCodePage() {
  const [searchValue, setSearchValue] = useState("");
  const [actualSearchValue, setActualSearchValue] = useState(""); // 실제 검색에 사용할 값
  const [exactMatch, setExactMatch] = useState(false); // 정확히 일치 체크박스
  const [actualExactMatch, setActualExactMatch] = useState(false); // 실제 검색에 사용할 정확히 일치
  const [employmentStatus, setEmploymentStatus] = useState("Y"); // 재직상태 (Y: 재직, N: 퇴사, "": 전체)
  const [actualEmploymentStatus, setActualEmploymentStatus] = useState("Y"); // 실제 검색에 사용할 값
  const [currentPage, setCurrentPage] = useState(0);
  const [allData, setAllData] = useState<EmployeeCode[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [tableVer, setTableVer] = useState(0);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedEmployeeCode, setSelectedEmployeeCode] = useState<EmployeeCode | undefined>(undefined);
  const deleteEmployeeCode = useEmployeeCodeDelete();
  const updateEmployeeCode = useEmployeeCodeResetPassword();

  // 재직상태 옵션
  const employmentStatusOptions = [
    { value: "", label: "전체" },
    { value: "Y", label: "재직" },
    { value: "N", label: "퇴사" },
  ];

  // 페이지네이션을 지원하는 useEmployeeCode 호출
  const {
    data: employeeCodeData,
    isLoading,
    error,
  } = useEmployeeCode({
    page: currentPage,
    size: 20,
    search: actualSearchValue,
    sort: "name,asc",
    isActive: actualEmploymentStatus,
    exactMatch: actualExactMatch,
  });

  // 검색어 또는 재직상태가 변경될 때 페이지 초기화만 수행 (데이터는 유지)
  useEffect(() => {
    setAllData([]);
    setCurrentPage(0);
    setTotalElements(0);
    setLoadingMore(false);
  }, [actualSearchValue, actualEmploymentStatus, actualExactMatch]);

  // 새로운 페이지 데이터가 로드되면 누적 데이터에 추가
  useEffect(() => {
    if (employeeCodeData && employeeCodeData.content && employeeCodeData.content.length > 0) {
      // 첫 번째 페이지(currentPage === 0)이거나 검색이 변경된 경우 기존 데이터 교체
      if (currentPage === 0) {
        setAllData(employeeCodeData.content);
        setTotalElements(employeeCodeData.totalElements || 0);
      } else {
        // 추가 페이지는 기존 데이터에 추가
        setAllData(prev => [...prev, ...employeeCodeData.content]);
      }
    } else if (employeeCodeData && (!employeeCodeData.content || employeeCodeData.content.length === 0)) {
      // 데이터가 없는 경우 빈 배열로 설정
      setAllData([]);
      setTotalElements(0);
    }
  }, [employeeCodeData, currentPage]);

  // hasMore 상태를 useMemo로 계산
  const hasMore = useMemo(() => {
    if (!employeeCodeData) return false;
    return !employeeCodeData.last && allData.length < totalElements;
  }, [employeeCodeData?.last, allData.length, totalElements]);

  // Table의 onLoadMore 콜백
  const handleTableLoadMore = useCallback(
    async (currentCount: number) => {
      if (!hasMore || loadingMore) {
        return;
      }

      // 현재 표시된 데이터가 누적된 데이터의 80% 이상일 때 다음 페이지 요청
      if (currentCount >= allData.length * 0.8) {
        setLoadingMore(true);
        setCurrentPage(prev => prev + 1);
      }
    },
    [hasMore, loadingMore, allData.length]
  );

  useEffect(() => {
    if (selectedRows.length === 1) {
      const selectedData = allData[selectedRows[0]];
      setSelectedEmployeeCode(selectedData);
    } else {
      setSelectedEmployeeCode(undefined);
    }
  }, [selectedRows, allData]);

  // 로딩 상태 업데이트
  useEffect(() => {
    if (employeeCodeData) {
      setLoadingMore(false);
    }
  }, [employeeCodeData]);

  // 테이블 컬럼 정의
  const columns: TableColumn[] = [
    { key: "name", header: "이름" },
    { key: "birthday", header: "생년월일" },
    { key: "mobileTel", header: "전화번호" },
    { key: "emergenctTel", header: "비상 연락망" },
    { key: "dutyName", header: "직위" },
    { key: "teamName", header: "부서" },
    { key: "address", header: "주소" },
    { key: "companyNames", header: "소속 회사" },
    {
      key: "isActive",
      header: "재직 상태",
      render: value => (value ? "재직" : "퇴사"),
    },
    {
      key: "lastLoginAt",
      header: "마지막 로그인 일시",
      render: value => {
        if (!value) return "";
        return value.split("T")[0];
      },
    },
    { key: "remark", header: "비고", bodyAlign: "left" },
  ];

  const handleReset = () => {
    setSearchValue("");
    setExactMatch(false);
    setEmploymentStatus("Y");
    setActualSearchValue("");
    setActualExactMatch(false);
    setActualEmploymentStatus("Y");
    setTableVer(v => v + 1);
  };

  const handleUpdate = (): void => {
    setAllData([]);
    setTotalElements(0);
    setCurrentPage(0);
    setSelectedRows([]);
    setSelectedEmployeeCode(undefined);
    setTableVer(v => v + 1);
  };

  const handleSearch = useCallback(() => {
    setActualSearchValue(searchValue);
    setActualExactMatch(exactMatch);
  }, [searchValue, exactMatch]);

  // 에러가 발생했을 때 표시할 내용
  if (error && allData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-red-500">
        데이터를 불러오는 중 오류가 발생했습니다.
      </div>
    );
  }

  const handleAddClick = () => {
    setSelectedRows([]);
    setIsModalOpen(true);
  };

  const handleUpdateClick = () => {
    if (selectedRows.length === 0) {
      toast.warning("수정할 사원을 선택해주세요.");
      return;
    }
    if (selectedRows.length > 1) {
      toast.warning("하나의 사원만 선택해주세요.");
      return;
    }
    setIsUpdateModalOpen(true);
    setTableVer(v => v + 1);
  };

  const handleResetPasswordClick = async () => {
    if (selectedRows.length === 0) {
      toast.warning("비밀번호 초기화할 사원을 선택해주세요.");
      return;
    }

    if (!window.confirm(`선택한 ${selectedRows.length}개의 사원의 비밀번호를 초기화하시겠습니까?`)) {
      return;
    }
    for (const index of selectedRows) {
      const employee = allData[index];
      if (employee?.name) {
        await updateEmployeeCode.mutateAsync(employee.name);
      }
    }
    handleUpdate();
  };

  const handleDeleteClick = async () => {
    if (selectedRows.length === 0) {
      toast.warning("삭제할 사원을 선택해주세요.");
      return;
    }

    if (!window.confirm(`선택한 ${selectedRows.length}개의 사원을 삭제하시겠습니까?`)) {
      return;
    }
    try {
      for (const index of selectedRows) {
        const employee = allData[index];
        if (employee?.name) {
          await deleteEmployeeCode.mutateAsync(employee.name);
        }
        handleUpdate();
      }
    } catch (error) {
      console.error("삭제 실패:", error);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <SearchBoxLayout>
        <div className="flex gap-3 items-end">
          <InputBox
            label="검색어"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            className="w-50"
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
          <SelectBox
            label="재직상태"
            value={employmentStatus}
            onChange={value => {
              setEmploymentStatus(value);
              setActualEmploymentStatus(value);
            }}
            options={employmentStatusOptions}
            className="w-32"
          />
          <div className="flex gap-[6px] ml-auto items-end">
            <CheckBox
              label="일치"
              labelPosition="right"
              checked={exactMatch}
              onChange={e => setExactMatch(e.target.checked)}
            />
            <Button variant="reset" onClick={handleReset}>
              초기화
            </Button>
            <Button variant="search" onClick={handleSearch}>
              조회
            </Button>
          </div>
        </div>
      </SearchBoxLayout>

      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <CountBox count={totalElements} />
          <div className="flex gap-2">
            <PermissionButton permission="canUpdate" variant="primary" width="120px" onClick={handleResetPasswordClick}>
              비밀번호 초기화
            </PermissionButton>
            <PermissionButton permission="canCreate" variant="etc" onClick={handleAddClick}>
              추가
            </PermissionButton>
            <PermissionButton permission="canUpdate" variant="etc" onClick={handleUpdateClick}>
              수정
            </PermissionButton>
            <PermissionButton permission="canDelete" variant="etc" onClick={handleDeleteClick}>
              삭제
            </PermissionButton>
            <PrintButton data={allData} columns={columns} title="사원코드" />
            <ExcelButton data={allData} columns={columns} fileName="사원코드" />
          </div>
        </div>
        <div className="flex-1 min-h-0">
          <Table
            key={`employee-code-table-v${tableVer}`}
            className="h-full"
            columns={columns}
            data={allData}
            showCheckbox={true}
            showRowNumber={true}
            selectedRows={selectedRows}
            onRowSelect={setSelectedRows}
            maxHeight="100%"
            infiniteScroll={true}
            initialLoadCount={20}
            loadMoreCount={20}
            hasMore={hasMore}
            onLoadMore={handleTableLoadMore}
            loadingMore={loadingMore}
          />
        </div>
        {isLoading && allData.length > 0 && <div className="text-center py-2 text-gray-500">새 페이지 로딩 중...</div>}
      </main>

      <AddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleUpdate} />
      <UpdateModal
        isOpen={isUpdateModalOpen}
        onClose={() => {
          setIsUpdateModalOpen(false);
          setSelectedEmployeeCode(undefined);
          setSelectedRows([]);
        }}
        onUpdate={handleUpdate}
        employeeData={selectedEmployeeCode}
      />
    </div>
  );
}
